import AdminPageHeader from "@/components/admin/page-header";
import { useLoadFeedback } from "@/hooks/use-load-feedback";
import { PageState } from "@/components/ui/page-state";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Eye, Printer, Search, TicketPercent, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatIDR } from "@/lib/api";
import { useOutlet } from "@/context/OutletContext";
import { useAdminSocket } from "@/lib/useAdminSocket";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ORDER_STATUS_FLOW, getNextOrderStatus, getOrderStatusBadgeClass, getOrderStatusLabel, getShortOrderNumber } from "@/features/orders/statusFlow";
import { PAYMENT_METHOD_LABELS, PAYMENT_METHOD_OPTIONS, UNPAID_FILTER, getPaymentBadge, getPaymentMethodLabel, getPaymentStatus, isOrderUnpaid } from "@/features/orders/paymentMeta";

const ORDER_FILTERS = ["all", UNPAID_FILTER, ...ORDER_STATUS_FLOW.map((step) => step.key), "cancelled"];
const UNASSIGNED_VALUE = "__unassigned__";

const FILTER_LABELS = { all: "Semua", [UNPAID_FILTER]: "Belum Lunas" };

export default function AdminOrders() {
  const { activeOutlet } = useOutlet();
  const outletId = activeOutlet?.id;
  const { loading, refreshing, hasLoaded, error: loadError, run } = useLoadFeedback(outletId);
  const [orders, setOrders] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [updatingOrderId, setUpdatingOrderId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [assignmentForm, setAssignmentForm] = useState({ assigned_waiter: "", assigned_cashier: "" });
  const [savingAssignment, setSavingAssignment] = useState(false);
  const [outletUsers, setOutletUsers] = useState([]);
  const [voucherCodeInput, setVoucherCodeInput] = useState("");
  const [savingVoucher, setSavingVoucher] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);

  const loadOrders = useCallback(() => run(async () => {
    if (!outletId) return;
    try {
      const { data } = await api.get("/orders", { params: { outlet_id: outletId } });
      setOrders(data || []);
    } catch {
      toast.error("Gagal memuat daftar pemesanan");
      throw new Error("Gagal memuat daftar pemesanan");
    }
  }), [outletId, run]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    const loadOutletUsers = async () => {
      if (!outletId) return;
      try {
        const { data } = await api.get(`/outlets/${outletId}/users`);
        setOutletUsers(data || []);
      } catch {
        toast.error("Gagal memuat daftar user outlet");
      }
    };
    loadOutletUsers();
  }, [outletId]);

  // Realtime: apply WS payloads straight into the list so settlement changes made
  // by another admin appear without a full reload.
  const handleRealtimeEvent = useCallback((msg) => {
    if (!msg?.event) return;
    if (msg.payload?.outlet_id && msg.payload.outlet_id !== outletId) return;
    if (msg.event === "order_updated" && msg.payload?.id) {
      const data = msg.payload;
      setOrders((prev) => prev.map((order) => (order.id === data.id ? data : order)));
      setSelectedOrder((prev) => (prev && prev.id === data.id ? data : prev));
    } else if (msg.event === "order_created") {
      loadOrders();
    }
  }, [loadOrders, outletId]);

  useAdminSocket(handleRealtimeEvent);

  const filteredOrders = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orders.filter((order) => {
      const passStatus =
        activeFilter === "all" ? true
        : activeFilter === UNPAID_FILTER ? isOrderUnpaid(order)
        : order.status === activeFilter;
      if (!passStatus) return false;
      const orderDate = dayjs(order.created_at);
      if (fromDate && (orderDate.isBefore(dayjs(fromDate), "day"))) return false;
      if (toDate && (orderDate.isAfter(dayjs(toDate), "day"))) return false;
      if (!q) return true;
      const source = `${order.order_number} ${order.table_number} ${order.customer_name || ""}`.toLowerCase();
      return source.includes(q);
    });
  }, [orders, activeFilter, fromDate, toDate, search]);

  const counts = useMemo(
    () => ORDER_FILTERS.reduce((acc, status) => {
      if (status === "all") acc[status] = orders.length;
      else if (status === UNPAID_FILTER) acc[status] = orders.filter((order) => isOrderUnpaid(order)).length;
      else acc[status] = orders.filter((order) => order.status === status).length;
      return acc;
    }, {}),
    [orders]
  );

  const advanceStatus = async (order) => {
    const nextStatus = getNextOrderStatus(order.status);
    if (!nextStatus) return;
    setUpdatingOrderId(order.id);
    try {
      const { data } = await api.put(`/orders/${order.id}/status`, { status: nextStatus });
      setOrders((prev) => prev.map((item) => (item.id === data.id ? data : item)));
      setSelectedOrder((prev) => (prev && prev.id === data.id ? data : prev));
      toast.success(`Status pesanan #${order.order_number} menjadi ${getOrderStatusLabel(nextStatus)}`);
    } catch {
      toast.error("Gagal memperbarui status pesanan");
    } finally {
      setUpdatingOrderId("");
    }
  };

  const printKitchen = (orderId) => {
    window.open(`/kitchen-print/${orderId}`, "_blank", "noopener,noreferrer");
  };

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setAssignmentForm({
      assigned_waiter: order.assigned_waiter || "",
      assigned_cashier: order.assigned_cashier || "",
    });
    setVoucherCodeInput("");
    setDetailOpen(true);
  };

  const saveAssignment = async () => {
    if (!selectedOrder) return;
    setSavingAssignment(true);
    try {
      const { data } = await api.put(`/orders/${selectedOrder.id}/assignment`, assignmentForm);
      setSelectedOrder(data);
      setOrders((prev) => prev.map((order) => (order.id === data.id ? data : order)));
      toast.success("Assignment waiter/kasir tersimpan");
    } catch {
      toast.error("Gagal menyimpan assignment waiter/kasir");
    } finally {
      setSavingAssignment(false);
    }
  };

  const applyVoucherToOrder = async () => {
    if (!selectedOrder) return;
    const code = voucherCodeInput.trim();
    if (!code) return toast.error("Masukkan kode voucher dulu");
    setSavingVoucher(true);
    try {
      const { data } = await api.put(`/orders/${selectedOrder.id}/voucher`, { voucher_code: code });
      setSelectedOrder(data);
      setOrders((prev) => prev.map((order) => (order.id === data.id ? data : order)));
      setVoucherCodeInput("");
      toast.success(`Voucher ${data.voucher_code} diterapkan — total diperbarui`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menerapkan voucher");
    } finally {
      setSavingVoucher(false);
    }
  };

  const removeVoucherFromOrder = async () => {
    if (!selectedOrder) return;
    setSavingVoucher(true);
    try {
      const { data } = await api.put(`/orders/${selectedOrder.id}/voucher`, { voucher_code: "" });
      setSelectedOrder(data);
      setOrders((prev) => prev.map((order) => (order.id === data.id ? data : order)));
      toast.success("Voucher dihapus — total kembali normal");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menghapus voucher");
    } finally {
      setSavingVoucher(false);
    }
  };

  const savePayment = async (patch) => {
    if (!selectedOrder) return;
    setSavingPayment(true);
    try {
      const { data } = await api.put(`/orders/${selectedOrder.id}/payment`, patch);
      setSelectedOrder(data);
      setOrders((prev) => prev.map((order) => (order.id === data.id ? data : order)));
      if (patch.payment_status === "paid") {
        toast.success(`Pesanan #${data.order_number} ditandai LUNAS`);
      } else if (patch.payment_status) {
        toast.success("Status pembayaran diperbarui");
      } else {
        toast.success(`Metode pembayaran disimpan: ${getPaymentMethodLabel(data.payment_method)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal menyimpan perubahan pembayaran");
    } finally {
      setSavingPayment(false);
    }
  };

  const selectedOrderSubtotal = useMemo(() => {
    if (!selectedOrder) return 0;
    return selectedOrder.subtotal || selectedOrder.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [selectedOrder]);

  const assignmentUserOptions = useMemo(() => {
    const names = outletUsers
      .map((user) => (user.name || user.email || "").trim())
      .filter(Boolean);
    if (selectedOrder?.assigned_waiter?.trim()) names.push(selectedOrder.assigned_waiter.trim());
    if (selectedOrder?.assigned_cashier?.trim()) names.push(selectedOrder.assigned_cashier.trim());
    return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b));
  }, [outletUsers, selectedOrder]);

  const detailPayBadge = selectedOrder ? getPaymentBadge(getPaymentStatus(selectedOrder)) : null;

  if (loading || (loadError && !hasLoaded)) return <PageState kind={loading ? "loading" : "error"} title={loading ? "Memuat data" : "Data belum tersedia"} description={loadError} onRetry={loadError ? loadOrders : undefined} />;

  return (
    <div aria-busy={refreshing} className="admin-page space-y-6" data-testid="admin-orders-page">
      {loadError && <PageState kind="error" title="Data belum dapat diperbarui" description={loadError} onRetry={loadOrders} />}
      <AdminPageHeader title="Pemesanan" description={<> Kelola status pesanan pelanggan • {activeOutlet?.name || "-"} </>} />

      <div className="surface-card p-4 space-y-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative max-w-md flex-1 min-w-[230px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9 bg-background" aria-label="Cari pesanan"
              placeholder="Cari nomor pesanan, meja, atau nama pelanggan"
              data-testid="search-orders-input"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Input aria-label="Tanggal mulai pemesanan" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} className="bg-background w-[160px]" data-testid="orders-from-date-input" />
            <Input aria-label="Tanggal akhir pemesanan" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} className="bg-background w-[160px]" data-testid="orders-to-date-input" />
            {(fromDate || toDate) && (
              <Button variant="outline" onClick={() => { setFromDate(""); setToDate(""); }} className="h-10" data-testid="orders-clear-date-filter-btn">
                Reset
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeFilter} onValueChange={setActiveFilter}>
          <TabsList className="h-auto flex flex-wrap">
            {ORDER_FILTERS.map((status) => (
              <TabsTrigger key={status} value={status} className="capitalize" data-testid={`orders-filter-${status}-btn`}>
                {FILTER_LABELS[status] || getOrderStatusLabel(status)} ({counts[status] || 0})
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <div className="surface-card overflow-x-auto">
        <div className="hidden xl:grid grid-cols-[minmax(120px,1fr)_70px_130px_110px_130px_230px] gap-3 border-b border-border bg-accent/40 px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">
          <span>Pesanan</span>
          <span>Meja</span>
          <span>Status</span>
          <span>Total</span>
          <span>Pembayaran</span>
          <span className="text-right">Aksi</span>
        </div>

        {loading ? (
          <p className="p-6 text-sm text-muted-foreground">Memuat data pemesanan...</p>
        ) : filteredOrders.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Belum ada pesanan pada filter ini.</p>
        ) : (
          filteredOrders.map((order) => {
            const statusStep = ORDER_STATUS_FLOW.find((step) => step.key === order.status);
            const nextLabel = statusStep?.nextLabel || "Lanjutkan";
            const payBadge = getPaymentBadge(getPaymentStatus(order));
            const payMethodLabel = getPaymentMethodLabel(order.payment_method);
            return (
              <div key={order.id} className="border-b border-border last:border-0 px-4 py-3" data-testid={`admin-order-row-${order.id}`}>
                <div className="xl:hidden space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-display font-bold text-sm">
                      #{getShortOrderNumber(order.order_number)}
                      {order.voucher_code ? (
                        <span className="ml-2 align-middle text-[10px] px-1.5 py-0.5 rounded-full bg-success/10 text-success font-normal" data-testid={`order-voucher-chip-${order.id}`}>
                          {order.voucher_code}
                        </span>
                      ) : null}
                    </p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${getOrderStatusBadgeClass(order.status)}`}>
                      {getOrderStatusLabel(order.status)}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {(order.order_type === 'room' ? 'Kamar' : 'Meja')} {order.table_number} {order.customer_name ? `• ${order.customer_name}` : ""}
                  </p>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="font-display font-bold text-primary truncate">{formatIDR(order.total)}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${payBadge.className}`}
                            data-testid={`order-payment-chip-${order.id}`}>{payMethodLabel} • {payBadge.label}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button variant="ghost" size="sm" className="h-8" onClick={() => openOrderDetail(order)} data-testid={`view-order-${order.id}-btn`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Detail
                      </Button>
                      <Button variant="outline" size="sm" className="h-8" onClick={() => printKitchen(order.id)}>
                        <Printer className="w-3.5 h-3.5 mr-1" /> Cetak
                      </Button>
                      {statusStep?.next && (
                        <Button size="sm" className="h-8" disabled={updatingOrderId === order.id} onClick={() => advanceStatus(order)}>
                          {updatingOrderId === order.id ? "Menyimpan..." : nextLabel}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="hidden xl:grid grid-cols-[minmax(120px,1fr)_70px_130px_110px_130px_230px] items-center gap-3 text-sm">
                  <div className="min-w-0">
                    <p className="font-medium truncate">
                      #{order.order_number}
                      {order.voucher_code ? (
                        <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-success/10 text-success font-normal" data-testid={`order-voucher-chip-desktop-${order.id}`}>
                          {order.voucher_code}
                        </span>
                      ) : null}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {order.customer_name || "-"} • {dayjs(order.created_at).format("DD MMM YYYY HH:mm")}
                    </p>
                  </div>
                  <p className="text-muted-foreground">{(order.order_type === 'room' ? 'Kamar' : 'Meja')} {order.table_number}</p>
                  <div>
                    <span className={`text-xs px-2 py-1 rounded-full ${getOrderStatusBadgeClass(order.status)}`}>
                      {getOrderStatusLabel(order.status)}
                    </span>
                  </div>
                  <p className="font-medium">{formatIDR(order.total)}</p>
                  <div className="min-w-0 space-y-0.5">
                    <span className={`inline-flex text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${payBadge.className}`}
                          data-testid={`order-payment-chip-desktop-${order.id}`}>{payMethodLabel} • {payBadge.label}</span>
                    {getPaymentStatus(order) === "paid" && order.paid_by ? (
                      <p className="text-[10px] text-muted-foreground truncate" data-testid={`order-paid-by-desktop-${order.id}`}>
                        oleh {order.paid_by}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-end gap-2">
                    <Button variant="ghost" size="sm" className="h-8" onClick={() => openOrderDetail(order)} data-testid={`view-order-desktop-${order.id}-btn`}>
                      <Eye className="w-3.5 h-3.5 mr-1" /> Detail
                    </Button>
                    <Button variant="outline" size="sm" className="h-8" onClick={() => printKitchen(order.id)}>
                      <Printer className="w-3.5 h-3.5 mr-1" /> Cetak
                    </Button>
                    {statusStep?.next && (
                      <Button size="sm" className="h-8" disabled={updatingOrderId === order.id} onClick={() => advanceStatus(order)}>
                        {updatingOrderId === order.id ? "Menyimpan..." : nextLabel}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="admin-dialog max-w-2xl max-h-[85vh] overflow-y-auto">
          {!selectedOrder ? null : (
            <>
              <DialogHeader>
                <DialogTitle>Detail Pesanan #{selectedOrder.order_number}</DialogTitle>
                <DialogDescription>
                  {(selectedOrder.order_type === 'room' ? 'Kamar' : 'Meja')} {selectedOrder.table_number} • {dayjs(selectedOrder.created_at).format("DD MMMM YYYY HH:mm")}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div className="bg-accent/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Status</p>
                    <p className={`inline-flex mt-1 text-xs px-2 py-0.5 rounded-full ${getOrderStatusBadgeClass(selectedOrder.status)}`}>
                      {getOrderStatusLabel(selectedOrder.status)}
                    </p>
                  </div>
                  <div className="bg-accent/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Pelanggan</p>
                    <p className="text-sm font-medium mt-1">{selectedOrder.customer_name || "-"}</p>
                  </div>
                  <div className="bg-accent/40 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-sm font-display font-bold text-primary mt-1">{formatIDR(selectedOrder.total)}</p>
                  </div>
                </div>

                {/* ── Pembayaran ── */}
                <div className="border border-border rounded-xl p-4 space-y-3" data-testid="order-payment-box">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="font-medium">Pembayaran</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${detailPayBadge.className}`} data-testid="order-payment-status">
                      {detailPayBadge.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
                    <div>
                      <Label htmlFor="order-payment-method-select">Metode Pembayaran</Label>
                      <Select
                        value={selectedOrder.payment_method || "cash"}
                        onValueChange={(value) => savePayment({ payment_method: value })}
                        disabled={savingPayment}
                      >
                        <SelectTrigger id="order-payment-method-select" className="mt-1 bg-background" data-testid="order-payment-method">
                          <SelectValue placeholder="Pilih metode..." />
                        </SelectTrigger>
                        <SelectContent className="admin-popup">
                          {PAYMENT_METHOD_OPTIONS.map((method) => (
                            <SelectItem key={`payment-method-${method}`} value={method} data-testid={`payment-method-option-${method}`}>
                              {PAYMENT_METHOD_LABELS[method]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedOrder.status !== "cancelled" && (
                      <div className="flex md:justify-end" data-testid="payment-status-actions">
                        {selectedOrder.payment_status === "paid" ? (
                          <Button variant="outline" size="sm" onClick={() => savePayment({ payment_status: "unpaid" })}
                                  disabled={savingPayment} data-testid="mark-unpaid-btn">
                            {savingPayment ? "Menyimpan..." : "Tandai Belum Bayar"}
                          </Button>
                        ) : (
                          <Button size="sm" onClick={() => savePayment({ payment_status: "paid" })}
                                  disabled={savingPayment} data-testid="mark-paid-btn"
                                  className="bg-success text-white hover:bg-success/90">
                            {savingPayment ? "Menyimpan..." : (<><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Tandai Lunas</>)}
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedOrder.payment_status === "paid" && selectedOrder.paid_at ? (
                    <p className="text-[11px] text-muted-foreground" data-testid="order-paid-meta">
                      Dibayar: {dayjs(selectedOrder.paid_at).format("DD MMM YYYY HH:mm")}
                      {selectedOrder.paid_by ? ` • oleh ${selectedOrder.paid_by}` : ""}
                    </p>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">
                      Pembayaran belum diterima — tandai lunas setelah tamu membayar di kasir.
                    </p>
                  )}
                </div>

                <div>
                  <h3 className="font-medium mb-2">Item Pesanan</h3>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item, index) => (
                      <div key={`${selectedOrder.id}-item-${index}`} className="flex items-start justify-between gap-3 border border-border rounded-lg px-3 py-2">
                        <div>
                          <p className="text-sm font-medium">{item.quantity}× {item.name}</p>
                          {item.notes ? <p className="text-xs text-muted-foreground italic mt-0.5">"{item.notes}"</p> : null}
                        </div>
                        <p className="text-sm font-medium">{formatIDR(item.price * item.quantity)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {selectedOrder.notes ? (
                  <div>
                    <Label htmlFor="adminorders-field-1">Catatan Pelanggan</Label>
                    <Textarea id="adminorders-field-1" value={selectedOrder.notes} readOnly className="bg-background mt-1" rows={2} />
                  </div>
                ) : null}

                <div className="border border-border rounded-xl p-4 space-y-3">
                  <h3 className="font-medium">Assign Operasional</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="assigned_waiter">Waiter</Label>
                      <Select
                        value={assignmentForm.assigned_waiter || UNASSIGNED_VALUE}
                        onValueChange={(value) => setAssignmentForm((prev) => ({ ...prev, assigned_waiter: value === UNASSIGNED_VALUE ? "" : value }))}
                      >
                        <SelectTrigger id="assigned_waiter" className="mt-1 bg-background" data-testid="assigned-waiter-select">
                          <SelectValue placeholder="Pilih waiter..." />
                        </SelectTrigger>
                        <SelectContent className="admin-popup">
                          <SelectItem value={UNASSIGNED_VALUE}>Belum di-assign</SelectItem>
                          {assignmentUserOptions.map((name) => (
                            <SelectItem key={`waiter-${name}`} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="assigned_cashier">Kasir</Label>
                      <Select
                        value={assignmentForm.assigned_cashier || UNASSIGNED_VALUE}
                        onValueChange={(value) => setAssignmentForm((prev) => ({ ...prev, assigned_cashier: value === UNASSIGNED_VALUE ? "" : value }))}
                      >
                        <SelectTrigger id="assigned_cashier" className="mt-1 bg-background" data-testid="assigned-cashier-select">
                          <SelectValue placeholder="Pilih kasir..." />
                        </SelectTrigger>
                        <SelectContent className="admin-popup">
                          <SelectItem value={UNASSIGNED_VALUE}>Belum di-assign</SelectItem>
                          {assignmentUserOptions.map((name) => (
                            <SelectItem key={`cashier-${name}`} value={name}>{name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={saveAssignment} disabled={savingAssignment} data-testid="save-order-assignment-btn">
                      {savingAssignment ? "Menyimpan..." : "Simpan Assignment"}
                    </Button>
                  </div>
                </div>

                <div className="border-t border-border pt-3 space-y-1.5 text-sm">
                  <Row label="Subtotal" value={formatIDR(selectedOrderSubtotal)} muted />
                  {(selectedOrder.discount_amount || 0) > 0 ? (
                    <div className="flex items-center justify-between" data-testid="order-detail-discount-row">
                      <span className="text-success font-medium">
                        Diskon{selectedOrder.discount_type === "percent" && selectedOrder.discount_value ? ` (${selectedOrder.discount_value}%)` : ""}
                      </span>
                      <span className="text-success font-medium">−{formatIDR(selectedOrder.discount_amount)}</span>
                    </div>
                  ) : null}
                  {(selectedOrder.tax_percent > 0 || selectedOrder.tax_amount > 0) ? (
                    <Row label={`Pajak (${selectedOrder.tax_percent || 0}%)`} value={formatIDR(selectedOrder.tax_amount || 0)} muted />
                  ) : null}
                  {(selectedOrder.service_percent > 0 || selectedOrder.service_amount > 0) ? (
                    <Row label={`Service (${selectedOrder.service_percent || 0}%)`} value={formatIDR(selectedOrder.service_amount || 0)} muted />
                  ) : null}
                  <div className="border-t border-border pt-2">
                    <Row label="Total" value={formatIDR(selectedOrder.total)} strong />
                  </div>
                </div>

                {!["completed", "cancelled"].includes(selectedOrder.status) && (
                  <div className="border border-border rounded-xl p-4 space-y-3">
                    <h3 className="font-medium flex items-center gap-2">
                      <TicketPercent className="w-4 h-4 text-primary" /> Voucher / Diskon
                    </h3>
                    {selectedOrder.voucher_code ? (
                      <div className="flex items-center justify-between gap-2 bg-success/10 border border-success/40 rounded-lg p-3" data-testid="order-voucher-applied-box">
                        <p className="text-sm min-w-0">
                          <b>{selectedOrder.voucher_code}</b>{" "}
                          <span className="text-muted-foreground text-xs">
                            ({selectedOrder.discount_type === "percent" ? `${selectedOrder.discount_value}%` : formatIDR(selectedOrder.discount_value)}) — potongan {formatIDR(selectedOrder.discount_amount)}
                          </span>
                        </p>
                        <Button variant="outline" size="sm" onClick={removeVoucherFromOrder} disabled={savingVoucher} data-testid="order-remove-voucher-btn">
                          Hapus
                        </Button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input placeholder="Kode voucher (bila ada)" value={voucherCodeInput}
                               onChange={(e) => setVoucherCodeInput(e.target.value.toUpperCase())}
                               className="bg-background uppercase" data-testid="order-voucher-input" />
                        <Button variant="outline" onClick={applyVoucherToOrder} disabled={savingVoucher}
                                className="flex-shrink-0" data-testid="order-apply-voucher-btn">
                          {savingVoucher ? "Cek..." : "Terapkan"}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => printKitchen(selectedOrder.id)}>
                  <Printer className="w-3.5 h-3.5 mr-1" /> Cetak Dapur
                </Button>
                <Button onClick={() => advanceStatus(selectedOrder)} disabled={updatingOrderId === selectedOrder.id || !getNextOrderStatus(selectedOrder.status)}>
                  {updatingOrderId === selectedOrder.id ? "Menyimpan..." : "Lanjutkan Status"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, muted, strong }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <span className={muted ? "text-muted-foreground" : ""}>{label}</span>
      <span className={strong ? "font-display font-bold text-primary" : (muted ? "text-muted-foreground" : "font-medium")}>{value}</span>
    </div>
  );
}
